import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_803bb858-9c3b-4809-94a4-95fda83a66ba/artifacts/t2u1oa1z_REVOQUOTA.png";

const navLinks = [
  { name: 'Servizi', href: '/servizi' },
  { name: 'Chi Siamo', href: '/chi-siamo' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'Normative', href: '/normative' },
  { name: 'Blog', href: '/blog' },
  { name: 'Contatti', href: '/contatti' },
];

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-revo-bg/95 backdrop-blur-md border-b border-revo-border' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="relative z-10" data-testid="navbar-logo">
            <img 
              src={LOGO_URL} 
              alt="RevoQuota" 
              className="h-10 md:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                className={`font-barlow text-sm uppercase tracking-widest transition-colors duration-300 relative group ${
                  location.pathname === link.href
                    ? 'text-revo-orange'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-revo-orange transition-all duration-300 ${
                  location.pathname === link.href ? 'w-full' : 'w-0 group-hover:w-full'
                }`} />
              </Link>
            ))}
          </div>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:flex items-center gap-6">
            <a 
              href="tel:800864621" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="navbar-phone"
            >
              <Phone className="w-4 h-4" />
              <span className="font-manrope text-sm">800 86 46 21</span>
            </a>
            <Link
              to="/contatti"
              data-testid="navbar-cta"
              className="bg-revo-orange text-white px-6 py-3 font-barlow font-bold uppercase tracking-widest text-sm hover:bg-revo-orange-dark transition-all duration-300 hover:shadow-[0_0_20px_rgba(255,102,0,0.3)]"
            >
              Richiedi Preventivo
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden relative z-10 p-2 text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-revo-bg border-b border-revo-border"
          >
            <div className="px-6 py-8 space-y-6">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={link.href}
                    data-testid={`mobile-nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
                    className={`block font-barlow text-2xl uppercase tracking-wide ${
                      location.pathname === link.href
                        ? 'text-revo-orange'
                        : 'text-white hover:text-revo-orange'
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="pt-6 border-t border-revo-border"
              >
                <Link
                  to="/contatti"
                  data-testid="mobile-cta"
                  className="block w-full text-center bg-revo-orange text-white px-6 py-4 font-barlow font-bold uppercase tracking-widest hover:bg-revo-orange-dark transition-colors"
                >
                  Richiedi Preventivo
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
